import React, { useState, useEffect, useContext, useMemo, useRef } from "react";
import { useParams } from "react-router-dom";
import { Box, Typography, Button } from "@mui/material";
import { getTasksDataApi, updateScheduleTasksApi } from "../../api/schedule";
import { getContractorsApi } from "../../api/member.js";
import { getSCurveApi } from "../../api/cost.js";
import { getOverAllBudgetApi } from "../../api/project.js";
import { useUser } from "../../hook/useUser.js";
import { useProject } from "../../hook/useProject.js";
import { calculateGanttDateRange } from "../../utils/schedule.js";
import { NotificationContext } from "../../component/popup/NotificationProvider";
import dayjs from "dayjs";
import cloneDeep from "lodash.clonedeep";
import LineBarChart from "../../component/chart/LineBarChart";
import ScheduleGantt from "../SchedulePage/components/ScheduleGantt";
import SelectComponent from "../../component/SelectComponent.js";
import DateConditions from "../../component/DateConditions.js";
import ManageCostPopup from "./components/ManageCostPopup.js";
import FullScreenLoading from "../../component/FullScreenLoading.js";
const CostPage = () => {
  const { projectId } = useParams();
  const { showSuccess, showError } = useContext(NotificationContext);
  const { user } = useUser(projectId);
  const projectDetails = useProject(projectId);
  const startDate = dayjs(projectDetails?.startDate);
  const endDate = dayjs(projectDetails?.endDate);
  const buttonStyle = {
    pr: 1.5,
    pl: 1,
    py: 0,
    lineHeight: "35px",
    borderRadius: "20px",
  };
  const titleStyle = {
    width: 280,
    minWidth: 280,
    height: 25,
    color: "#fff",
    backgroundSize: "cover",
    display: "flex",
    alignItems: "center",
    pl: 2,
    cursor: "pointer",
    gap: 1,
  };
  const headers = [
    { label: "Task ID", width: "100px" },
    { label: "Task Name", width: "170px" },
    { label: "", width: "20px" },
    { label: "Plan Start", width: "85px" },
    { label: "Plan End", width: "85px" },
    { label: "Actual Start", width: "85px" },
    { label: "Actual End", width: "85px" },
    { label: "Contractor", width: "100px" },
    { label: "Planned Value", width: "130px" },
    { label: "Earned Value", width: "100px" },
    { label: "Additional Actual Cost", width: "130px" },
    { label: "Actual Cost", width: "100px" },
  ];
  const [tabs, setTabs] = useState([]);
  const [state, setState] = useState({
    currentTab: "all",
    startDate: dayjs(),
    endDate: dayjs(),
    currentDate: dayjs(),
    chartFilterOptions: [
      { value: "SPI", label: "Schedule Performance Index (SPI)" },
      { value: "CPI", label: "Cost Performance Index (CPI)" },
    ],
    chartFilterValue: [],
    showPopup: false,
    editCurrentCost: false,
    contractorData: [],
    selectedRowId: "",
  });
  const [chartData, setChartData] = useState([]);
  const [editableColumns, setEditableColumns] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [taskTableData, setTaskTableData] = useState([]);
  const [tempTableData, setTempTableData] = useState([]);
  const [changeRows, setChangeRows] = useState([]);
  const [overallBudget, setOverallBudget] = useState(null);
  const tempTableDataRef = useRef(tempTableData);

  useEffect(() => {
    tempTableDataRef.current = tempTableData;
  }, [tempTableData]);

  const currentContractorData = useMemo(() => {
    if (state.currentTab === "all") {
      return overallBudget ? [overallBudget] : [];
    }
    const selectedContractor = state.contractorData.find((c) => c.name === state.currentTab);
    if (selectedContractor) {
      return [
        {
          ...selectedContractor,
          budget: selectedContractor.budget ?? 0,
        },
      ];
    }
    return [];
  }, [state.currentTab, state.contractorData, overallBudget]);

  useEffect(() => {
    if (user) {
      getContractTypeData();
    }
  }, [user]);

  useEffect(() => {
    if (!projectDetails) return;
    getTaskData();
  }, [projectDetails]);

  useEffect(() => {
    if (state.currentTab === "all" && overallBudget === null) {
      return;
    }
    getCostData();
  }, [state.currentDate, state.currentTab, state.contractorData, overallBudget]);

  useEffect(() => {
    if (!projectId) return;
    getOverallBudget();
  }, [state.currentTab, state.contractorData, projectId]);

  // const updateTaskRecursively = (tasks, scheduleId, fieldName, newValue) => {
  //   let updatedChangeRows = [];
  //   const updatedTasks = tasks.map((task) => {
  //     if (task.scheduleId === scheduleId) {
  //       const updatedTask = { ...task, [fieldName]: newValue };
  //       updatedChangeRows.push(updatedTask);
  //       return {
  //         ...updatedTask,
  //         children: task.children ? updateTaskRecursively(task.children, scheduleId, fieldName, newValue).updatedTasks : [],
  //       };
  //     }
  //     if (task.children && task.children.length > 0) {
  //       const result = updateTaskRecursively(task.children, scheduleId, fieldName, newValue);
  //       updatedChangeRows = [...updatedChangeRows, ...result.updatedChangeRows];
  //       return {
  //         ...task,
  //         children: result.updatedTasks,
  //       };
  //     }
  //     return task;
  //   });
  //   return {
  //     updatedTasks,
  //     updatedChangeRows,
  //   };
  // };

  const updateTaskRecursively = (tasks, scheduleId, fieldName, newValue) => {
    let updatedChangeRows = [];

    const updatedTasks = tasks.map((task) => {
      if (task.scheduleId === scheduleId) {
        const updatedTask = {
          ...task,
          [fieldName]: newValue,
        };
        updatedChangeRows.push(updatedTask);
        return task.children
          ? { ...updatedTask, children: updateTaskRecursively(task.children, scheduleId, fieldName, newValue).updatedTasks }
          : updatedTask;
      }
      if (task.children && task.children.length > 0) {
        const result = updateTaskRecursively(task.children, scheduleId, fieldName, newValue);
        updatedChangeRows = [...updatedChangeRows, ...result.updatedChangeRows];
        return {
          ...task,
          children: result.updatedTasks,
        };
      }
      return task;
    });
    return {
      updatedTasks,
      updatedChangeRows,
    };
  };

  const handleRowEdit = (scheduleId, fieldName, newValue) => {
    const tempCopy = cloneDeep(tempTableDataRef.current);
    // const { updatedTasks, updatedChangeRows } = updateTaskRecursively(taskTableData, scheduleId, fieldName, newValue);
    const { updatedTasks, updatedChangeRows } = updateTaskRecursively(tempCopy, scheduleId, fieldName, newValue);
    // setTaskTableData(updatedTasks);
    setTempTableData(updatedTasks);
    setChangeRows((prev) => [...prev, ...updatedChangeRows]);
  };

  const getOverallBudget = async () => {
    try {
      const result = await getOverAllBudgetApi(projectId);
      setOverallBudget({
        name: "Overall Budget",
        budget: result?.data?.overallBudget ?? 0,
      });
    } catch (error) {
      setOverallBudget({
        name: "Overall Budget",
        budget: 0,
      });
    }
  };

  const getTaskData = async () => {
    const result = await getTasksDataApi(projectId, undefined, true);
    const data = result?.data;
    if (!data || !Array.isArray(data)) {
      showError("Invalid or empty task data received.");
      return;
    }
    const { startDate, endDate } = calculateGanttDateRange(data, projectDetails);
    setState((prevState) => ({
      ...prevState,
      startDate,
      endDate,
    }));
    setTaskTableData(data);
    setTempTableData(cloneDeep(data));
    setIsLoading(false);
  };

  const getCostData = async () => {
    try {
      const response = await getSCurveApi(projectId, state.currentDate.format("YYYY-MM-DD"), state.currentTab);
      const data = response.data;
      let budget;
      if (state.currentTab === "all") {
        budget = overallBudget?.budget ?? 0;
        // budget = state.contractorData.reduce((acc, contractor) => acc + (contractor.budget || 0), 0);
      } else {
        const selectedContractor = state.contractorData.find((contractor) => contractor.name === state.currentTab);
        budget = selectedContractor ? selectedContractor.budget : 0;
      }
      const budgetData = data.map((d) => d.time).map((t) => ({ time: t, value: budget, name: "Budget Line" }));
      setChartData([...data, ...budgetData]);
    } catch {
      showError("Received cost data occurred error.");
    }
  };

  const getContractTypeData = async () => {
    try {
      const result = await getContractorsApi(projectId);
      const data = result?.data;
      if (data) {
        const filteredByCostModule = data.filter((d) => d.costModule === true);
        let contracts, newState;
        if (user.userRole === "Contractor") {
          const filteredData = filteredByCostModule.filter((d) => d.name === user.company);
          contracts = [...new Set(filteredData.map((d) => d.name))];
          newState = {
            currentTab: contracts[0],
            contractorData: filteredData,
          };
          setTabs(
            contracts.map((c) => ({
              title: `Earned Value - ${c}`,
              contract: c,
            }))
          );
        } else {
          contracts = [...new Set(filteredByCostModule.map((d) => d.name))];
          newState = {
            currentTab: "all",
            contractorData: filteredByCostModule,
          };
          setTabs([
            { title: `Earned Value - Overall Contract`, contract: "all" },
            ...contracts.map((c) => ({
              title: `Earned Value - ${c}`,
              contract: c,
            })),
          ]);
        }
        setState((prevState) => ({
          ...prevState,
          ...newState,
        }));
      }
    } catch {
      showError("Received contract type data occurred error.");
    }
  };

  const getChartOptions = () => {
    const selectedKpis = state.chartFilterValue;
    const seriesConfig = {};
    const markLineValue = dayjs(state.currentDate).format("MMM YYYY");
    if (selectedKpis.includes("SPI") || selectedKpis.includes("Schedule Performance Index (SPI)")) {
      seriesConfig["SPI"] = {
        yAxisIndex: 1,
        color: "#008AD8",
        type: "line",
      };
    }
    if (selectedKpis.includes("CPI") || selectedKpis.includes("Cost Performance Index (CPI)")) {
      seriesConfig["CPI"] = {
        yAxisIndex: 1,
        color: "#0033A0",
        type: "line",
      };
    }
    seriesConfig["Plan Value"] = {
      color: "#A9E2CD",
      type: "line",
    };
    seriesConfig["Earned Value"] = {
      color: "#318E6C",
      type: "line",
    };
    seriesConfig["Actual Cost"] = {
      color: "#FF8080",
      markLine: { xAxis: markLineValue, color: "#C00F0C" },
      type: "line",
    };
    seriesConfig["Budget Line"] = {
      color: "#717273",
      type: "line",
    };
    // seriesConfig["Forecast Line"] = {
    //   color: "#FF8080",
    //   type: "line",
    // };
    return seriesConfig;
  };

  const chartFilterChange = (value) => {
    setState((prevState) => ({
      ...prevState,
      chartFilterValue: value,
    }));
  };

  const handleCurrentDateChange = (newCurrentDate) => {
    if (newCurrentDate && startDate && newCurrentDate.isBefore(startDate)) {
      setState((prevState) => ({
        ...prevState,
        currentDate: startDate,
      }));
    } else if (newCurrentDate && endDate && newCurrentDate.isAfter(endDate)) {
      setState((prevState) => ({
        ...prevState,
        currentDate: endDate,
      }));
    } else {
      setState((prevState) => ({
        ...prevState,
        currentDate: newCurrentDate,
      }));
    }
  };

  const handlePrevDay = () => {
    setState((prevState) => ({
      ...prevState,
      currentDate: prevState.currentDate ? prevState.currentDate.subtract(1, "day") : dayjs().subtract(1, "day"),
    }));
  };

  const handleNextDay = () => {
    setState((prevState) => ({
      ...prevState,
      currentDate: prevState.currentDate ? prevState.currentDate.add(1, "day") : dayjs().add(1, "day"),
    }));
  };

  const handleClosePopup = () => {
    setState((prevState) => ({
      ...prevState,
      showPopup: false,
    }));
  };

  const handleFileUploadSuccess = () => {
    getTaskData();
  };

  const transformChangeRows = (changeRows) => {
    return changeRows.map((row) => ({
      scheduleId: row.scheduleId,
      actualStart: row.actualStart || null,
      actualEnd: row.actualEnd || null,
      planValue: row.planValue || null,
      additionalActualCost: row.additionalActualCost || null,
    }));
  };

  const toggleEditCost = () => {
    setState((prevState) => ({
      ...prevState,
      editCurrentCost: !prevState.editCurrentCost,
    }));

    if (!state.editCurrentCost) {
      setEditableColumns(["Actual Start", "Actual End", "Planned Value", "Additional Actual Cost"]);
    } else {
      setEditableColumns([]);
      setTempTableData(cloneDeep(taskTableData));
      setChangeRows([]);
    }
  };

  const saveColumnData = async () => {
    try {
      const uniqueChangeRows = [...new Map(changeRows.map((row) => [row.scheduleId, row])).values()];
      const transformedChangeRows = transformChangeRows(uniqueChangeRows);

      const result = await updateScheduleTasksApi(projectId, transformedChangeRows);
      if (result) {
        await getCostData();
        await getTaskData();
        showSuccess("The Cost has been successfully updated.");
        setState((prevState) => ({
          ...prevState,
          editCurrentCost: false,
        }));
        setEditableColumns([]);
        setChangeRows([]);
      }
    } catch (error) {
      showError("The Cost update failed.");
    }
  };

  // const updateColumnData = async () => {
  //   try {
  //     setState((prevState) => ({
  //       ...prevState,
  //       editCurrentCost: !prevState.editCurrentCost,
  //     }));
  //     setEditableColumns(["Actual Start", "Actual End", "Planned Value", "Additional Actual Cost"]);
  //     if (state.editCurrentCost) {
  //       const uniqueChangeRows = [...new Map(changeRows.map((row) => [row.scheduleId, row])).values()];
  //       const transformedChangeRows = transformChangeRows(uniqueChangeRows);
  //       setChangeRows(uniqueChangeRows);
  //       const result = await updateScheduleTasksApi(projectId, transformedChangeRows);
  //       if (result) {
  //         showSuccess("The Cost has been successfully updated.");
  //         setEditableColumns([]);
  //         getTaskData();
  //         getCostData();
  //       }
  //     }
  //   } catch {
  //     showError("The Cost update failed.");
  //   }
  // };

  const handleUpdateBudget = (updatedContractor) => {
    setState((prevState) => {
      const updatedContractorData = prevState.contractorData.map((contractor) =>
        contractor.projectContractorId === updatedContractor[0].projectContractorId ? updatedContractor[0] : contractor
      );
      return {
        ...prevState,
        contractorData: updatedContractorData,
      };
    });
    getCostData();
  };

  const handleRowClick = (row) => {
    setState((prevState) => ({
      ...prevState,
      selectedRowId: row.taskId,
    }));
  };

  return (
    <>
      <Box
        sx={{
          position: "relative",
          height: "40vh",
          border: "1px solid #ccc",
          borderRadius: "10px",
        }}
      >
        <Box sx={{ position: "absolute", right: "20px", top: "5px" }}>
          <SelectComponent
            options={state.chartFilterOptions}
            value={state.chartFilterValue}
            onChange={chartFilterChange}
            isMultiSelect
            required={false}
            placeholder={"Select a KPI..."}
          />
        </Box>
        <Box
          sx={{
            width: "calc(100% - 180px)",
            position: "absolute",
            overflowX: "auto",
            display: "flex",
            gap: 2,
            "::-webkit-scrollbar": {
              height: "8px",
            },
            "::-webkit-scrollbar-thumb": {
              border: "1px solid transparent",
            },
          }}
        >
          {tabs.map((tab) => (
            <Box
              key={tab.title}
              sx={{
                ...titleStyle,
                backgroundImage: `url(assets/background/${state.currentTab === tab.contract ? "titlebg" : "greytitlebg"}.svg)`,
              }}
              onClick={() => {
                setState((prevState) => ({
                  ...prevState,
                  currentTab: tab.contract,
                }));
              }}
            >
              <img src={"assets/icons/cost.svg"} style={{ height: 20 }} alt={"cost"} />
              <Typography component="span">{tab.title}</Typography>
            </Box>
          ))}
        </Box>
        <Box sx={{ height: "100%", pt: "40px" }}>
          <LineBarChart data={chartData} options={getChartOptions()} legendField={"name"} xAxisField={"time"} yAxisField={"value"}></LineBarChart>
        </Box>
      </Box>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          my: 1.4,
        }}
      >
        <Button
          onClick={() =>
            setState((prevState) => ({
              ...prevState,
              showPopup: true,
            }))
          }
          variant="contained"
          color="secondary"
          sx={{
            ...buttonStyle,
          }}
          startIcon={<img src="assets/buttons/set.svg" alt="Manage Schedule" />}
        >
          Manage Cost
        </Button>
        <DateConditions
          startDate={startDate}
          currentDate={state.currentDate}
          endDate={endDate}
          handleCurrentDateChange={handleCurrentDateChange}
          handlePrevDay={handlePrevDay}
          handleNextDay={handleNextDay}
          disabledStartEnd
          text="Selection:"
        ></DateConditions>
        <Box sx={{ minWidth: 160, textAlign: "right" }}>
          {/* <Button onClick={() => updateColumnData()} variant="contained" color="success" sx={{ py: "2px" }}>
            {state.editCurrentCost ? "Save" : "Update"}
          </Button> */}
          <Button onClick={state.editCurrentCost ? saveColumnData : toggleEditCost} variant="contained" color="success" sx={{ py: "2px" }}>
            {state.editCurrentCost ? "Save" : "Update"}
          </Button>
          {state.editCurrentCost && (
            <Button
              onClick={() => {
                setState((prevState) => ({
                  ...prevState,
                  editCurrentCost: false,
                }));
                setEditableColumns([]);
                setTempTableData(cloneDeep(taskTableData));
                setChangeRows([]);
              }}
              variant="outlined"
              color="inherit"
              sx={{ py: "2px", ml: 2 }}
            >
              Cancel
            </Button>
          )}
        </Box>
      </Box>
      <Box sx={{ height: "44vh" }}>
        <ScheduleGantt
          treeTableHeaders={headers}
          editableColumns={editableColumns}
          // tasks={state.currentTab === "all" ? taskTableData : taskTableData?.filter((t) => t.contractor.includes(state.currentTab))}
          tasks={state.currentTab === "all" ? tempTableData : tempTableData?.filter((t) => t.contractor.includes(state.currentTab))}
          startDate={state.startDate}
          endDate={state.endDate}
          currentDate={state.currentDate}
          setCurrentDate={(d) =>
            setState((prevState) => ({
              ...prevState,
              currentDate: d ? dayjs(d) : null,
            }))
          }
          onRowEdit={handleRowEdit}
          taskTableLayout={state.editCurrentCost}
          showButton
          selectedRowId={state.selectedRowId}
          handleRowClick={handleRowClick}
          tableRowHeight={state.editCurrentCost ? 35 : 24.65}
          estimateSize={state.editCurrentCost ? 35 : 24.65}
        ></ScheduleGantt>
      </Box>
      <ManageCostPopup
        open={state.showPopup}
        onClose={handleClosePopup}
        onUploadSuccess={handleFileUploadSuccess}
        user={user}
        onUpdateBudget={handleUpdateBudget}
        // contractorData={state.contractorData}
        contractorData={currentContractorData}
      />
      <FullScreenLoading isLoading={isLoading} />
    </>
  );
};
export default CostPage;
